import { FinancialAidProgram, FinancialAidResponse, FinancialAidFilters } from "../types";
import { getApiBaseUrl, getApiKey } from "../utils/config";

class FinancialAidApiService {
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    // Get API configuration dynamically
    const baseUrl = getApiBaseUrl();
    const apiKey = getApiKey();
    
    const url = `${baseUrl}${endpoint}`;
    const response = await fetch(url, {
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${apiKey}`,
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Get financial aid programs with optional filtering
  async getFinancialAidPrograms(filters: FinancialAidFilters = {}): Promise<FinancialAidResponse> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });

    const endpoint = `/api/financial-aid/programs${params.toString() ? `?${params.toString()}` : ''}`;
    return this.request<FinancialAidResponse>(endpoint);
  }

  // Get financial aid program by ID
  async getFinancialAidProgramById(programId: string): Promise<{ data: FinancialAidProgram; program_id: string }> {
    return this.request<{ data: FinancialAidProgram; program_id: string }>(`/api/financial-aid/programs/${programId}`);
  }

  // Search financial aid programs
  async searchFinancialAidPrograms(
    query: string,
    limit: number = 25,
    skip: number = 0
  ): Promise<FinancialAidResponse> {
    const params = new URLSearchParams({
      q: query,
      limit: limit.toString(),
      skip: skip.toString(),
    });
    
    return this.request<FinancialAidResponse>(`/api/financial-aid/search?${params.toString()}`);
  }

  // Get financial aid categories
  async getFinancialAidCategories(): Promise<{
    categories: string[];
    category_counts: Record<string, number>;
    total_categories: number;
  }> {
    return this.request(`/api/financial-aid/categories`);
  }

  // Get financial aid types
  async getFinancialAidTypes(): Promise<{
    types: string[];
    type_counts: Record<string, number>;
    total_types: number;
  }> {
    return this.request(`/api/financial-aid/types`);
  }

  // Get financial aid providers
  async getFinancialAidProviders(): Promise<{
    providers: string[];
    provider_counts: Record<string, number>;
    total_providers: number;
  }> {
    return this.request(`/api/financial-aid/providers`);
  }

  // Get financial aid statistics
  async getFinancialAidStats(): Promise<any> {
    return this.request(`/api/financial-aid/stats`);
  }

  // Upload financial aid data (admin function)
  async uploadFinancialAidData(overwrite: boolean = false): Promise<{
    success: boolean;
    uploaded_count: number;
    updated_count: number;
    error_count: number;
    total_processed: number;
    timestamp: string;
  }> {
    const params = new URLSearchParams({
      overwrite: overwrite.toString(),
    });
    
    return this.request(`/api/financial-aid/upload?${params.toString()}`, {
      method: 'POST',
    });
  }
}

export const financialAidApiService = new FinancialAidApiService(); 